import React, { useState, useMemo, useEffect } from 'react';
import { 
  UsersIcon, 
  MagnifyingGlassIcon, 
  PlusCircleIcon,
  PencilIcon, 
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { TextInput, Button, Select } from '@mantine/core';
import { modals } from '@mantine/modals';
import { errorToast, successToast } from '../../lib/helpers';
import haxios from '../../lib/haxios';
import useApi from '../../hooks/useApi';
import moment from 'moment';
import BranchDropdown from '../../components/BranchDropdown';
import DataTable from '../../components/AdvancedReports/shared/DataTable';

const Staff = () => {
  const {
    data: staffData = [],
    isLoading: isStaffLoading,
    refetch
  } = useApi({
    url: '/salesassistnew/ustafferators'
  });

  const [searchValue, setSearchValue] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [positionFilter, setPositionFilter] = useState('all');
  const [allowLoginFilter, setAllowLoginFilter] = useState('all');

  // Filter staff data
  const filteredStaff = useMemo(() => {
    if (!staffData || staffData.length === 0) return [];
    
    return staffData.filter((staff) => {
      const matchesSearch = searchValue === '' || 
        `${staff.FName || ''} ${staff.SName || ''} ${staff.Branch || ''} ${staff.Position || ''}`.toLowerCase().includes(searchValue.toLowerCase());
      
      const matchesBranch = !selectedBranch || staff.Branch === selectedBranch;
      
      const matchesPosition = positionFilter === 'all' || staff.Position === positionFilter;
      
      const matchesLogin = allowLoginFilter === 'all' || 
        (allowLoginFilter === 'yes' && staff.AllowLogin) ||
        (allowLoginFilter === 'no' && !staff.AllowLogin);
      
      return matchesSearch && matchesBranch && matchesPosition && matchesLogin;
    });
  }, [staffData, searchValue, selectedBranch, positionFilter, allowLoginFilter]);

  // Get unique positions for filter
  const uniquePositions = useMemo(() => {
    if (!staffData || staffData.length === 0) return [];
    const positions = [...new Set(staffData.map(s => s.Position).filter(Boolean))];
    return positions.sort();
  }, [staffData]);

  const handleAddStaff = () => {
    modals.openContextModal({
      modal: 'staffEditModal',
      title: 'Add a staff member',
      centered: true,
      innerProps: {
        submit: (data) => haxios.post(`/salesassistnew/ustafferators`, data),
        refetch
      }
    });
  };

  const handleEditStaff = (staff) => {
    modals.openContextModal({
      modal: 'staffEditModal',
      title: 'Edit a staff member',
      centered: true,
      innerProps: {
        defaultValues: {
          FName: staff.FName,
          SName: staff.SName,
          Dob: moment(staff.Dob).toDate(),
          Position: staff.Position,
          Branch: staff.Branch,
          AllowLogin: staff.AllowLogin,
          Email: staff.Email,
          Username: staff.Username,
          Password: staff.Password,
          IsAdmin: staff.IsAdmin,
          Inits: staff.Inits,
          SalesTarget: staff.SalesTarget,
          StaffCategory: staff.StaffCategory || '',
          MonthlyCostPerHead: staff.MonthlyCostPerHead || null,
          HolidayStartDate: staff.HolidayStartDate,
          HolidayAllocation: staff.HolidayAllocation
        },
        submit: (data) => haxios.put(`/salesassistnew/ustafferators/${staff.StaffId}`, data),
        refetch
      }
    });
  };

  const handleDeleteStaff = (staff) => {
    modals.openConfirmModal({
      title: 'Delete Staff Member',
      children: (
        <p className="text-sm text-gray-600">
          Are you sure you want to delete {staff.FName} {staff.SName}? This action cannot be undone.
        </p>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await haxios.delete(`/salesassistnew/ustafferators/${staff.StaffId}`);
          successToast('Staff member deleted successfully');
          refetch();
        } catch (error) {
          errorToast('Failed to delete staff member');
        }
      }
    });
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (
        <span className="font-medium text-gray-900">
          {`${row.FName || ''} ${row.SName || ''}`.trim() || 'N/A'}
        </span>
      )
    },
    {
      key: 'Branch',
      label: 'Branch',
      sortable: true
    },
    {
      key: 'Position',
      label: 'Position',
      sortable: true
    },
    {
      key: 'StaffCategory',
      label: 'Category',
      sortable: true,
      render: (value) => value || '—'
    },
    {
      key: 'MonthlyCostPerHead',
      label: 'Monthly Cost',
      sortable: true,
      render: (value) => value ? `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'
    },
    {
      key: 'HourlyRate',
      label: 'Hourly Rate',
      sortable: true,
      render: (value) => value ? `£${parseFloat(value).toFixed(2)}` : '—'
    },
    {
      key: 'AllowLogin',
      label: 'Login',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      key: 'SalesTarget',
      label: 'Sales Target',
      sortable: true,
      render: (value) => value ? `£${parseFloat(value).toLocaleString()}` : '—'
    },
    {
      key: 'HolidayStartDate',
      label: 'Holiday Start',
      sortable: true,
      render: (value) => value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    {
      key: 'HolidayAllocation',
      label: 'Holiday Days',
      sortable: true,
      render: (value) => value || '—'
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEditStaff(row)}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            title="Edit staff member"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteStaff(row)}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
            title="Delete staff member"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  // Transform data for DataTable
  const tableData = useMemo(() => {
    return filteredStaff.map(staff => ({
      ...staff,
      name: `${staff.FName || ''} ${staff.SName || ''}`.trim(),
      onlineStatus: staff.IsOnline
    }));
  }, [filteredStaff]);

  if (isStaffLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="px-6 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <UsersIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Staff Management</h1>
              <p className="text-sm text-gray-600 mt-1">Manage staff members, positions, and access</p>
            </div>
          </div>
          <Button
            onClick={handleAddStaff}
            leftSection={<PlusCircleIcon className="w-5 h-5" />}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            Add Staff
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-blue-500 p-6 hover:shadow-md transition-shadow">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-600">
                  Total Staff
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <UsersIcon className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {staffData.length}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-green-500 p-6 hover:shadow-md transition-shadow">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-600">
                  Online Now
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-green-600">
                {staffData.filter(s => s.IsOnline).length}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-indigo-500 p-6 hover:shadow-md transition-shadow">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-600">
                  Can Login
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <CheckCircleIcon className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-indigo-600">
                {staffData.filter(s => s.AllowLogin).length}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 border-l-purple-500 p-6 hover:shadow-md transition-shadow">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-600">
                  Unique Positions
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <ClockIcon className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="text-3xl font-bold text-purple-600">
                {uniquePositions.length}
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <TextInput
              placeholder="Search staff..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              leftSection={<MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />}
              className="flex-1"
            />
            <BranchDropdown branch={selectedBranch} setBranch={setSelectedBranch} />
            <Select
              placeholder="Filter by position"
              value={positionFilter}
              onChange={setPositionFilter}
              data={[
                { value: 'all', label: 'All Positions' },
                ...uniquePositions.map(pos => ({ value: pos, label: pos }))
              ]}
            />
            <Select
              placeholder="Filter by login access"
              value={allowLoginFilter}
              onChange={setAllowLoginFilter}
              data={[
                { value: 'all', label: 'All' },
                { value: 'yes', label: 'Can Login' },
                { value: 'no', label: 'Cannot Login' }
              ]}
            />
          </div>
        </div>

        {/* Staff Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Staff Members ({filteredStaff.length})
            </h2>
          </div>
          <DataTable
            data={tableData}
            loading={isStaffLoading}
            columns={columns}
            searchable={false}
            sortable={true}
            pagination={true}
            pageSize={10}
            emptyMessage="No staff members found"
          />
        </div>
      </div>
    </div>
  );
};

export default Staff;
